import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Mail, MapPin, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import emailjs from '@emailjs/browser';

// Importações do Firebase
import { db } from "@/lib/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

// Inicializar o EmailJS
emailjs.init("oCHXgFEpKF_FNSq6-");

export const Contact = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // CORREÇÃO: Definir a variável corretamente no escopo do try
    try {
      const formDataObject = {
        name: formData.get('name') as string,
        email: formData.get('email') as string,
        message: formData.get('message') as string,
      };

      console.log("Dados do formulário:", formDataObject);

      // 1. Salvar no Firestore
      await addDoc(collection(db, "contacts"), {
        ...formDataObject,
        createdAt: serverTimestamp()
      });
      
      // 2. Enviar email para ADMIN (você)
      const adminTemplateParams = {
        from_name: formDataObject.name,
        from_email: formDataObject.email,
        message: formDataObject.message,
        to_email: "contato@datavizbi.com.br",
        reply_to: formDataObject.email
      };

      await emailjs.send(
        'service_n7kze9k',
        'template_pmej89o', // Template para admin
        adminTemplateParams
      );
      
      // 3. Enviar email de CONFIRMAÇÃO para o CLIENTE
      const clientTemplateParams = {
        from_name: "Equipe DataViz BI",
        from_email: "contato@datavizbi.com.br", // DEVE ser seu email
        to_email: formDataObject.email, // Email do cliente
        client_name: formDataObject.name,
        message: formDataObject.message,
        reply_to: "contato@datavizbi.com.br"
      };

      // TEMPORARIAMENTE: Usar o mesmo template para teste
      // Substitua pelo novo template ID quando criar
      await emailjs.send(
        'service_n7kze9k',
        'template_v1jh4tp', // Use o mesmo template por enquanto
        clientTemplateParams
      );
      
      toast({
        title: "Mensagem enviada com sucesso!",
        description: "Entraremos em contato em breve. Você receberá um email de confirmação.",
      });
      
      form.reset();
    } catch (error) {
      console.error('Erro ao enviar:', error);
      toast({
        title: "Erro ao enviar mensagem",
        description: "Tente novamente mais tarde.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <section id="contato" className="py-20 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Entre em <span className="gradient-text">Contato</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Pronto para transformar seus dados? Vamos conversar sobre seu projeto
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <Card className="border-0 bg-gradient-card shadow-lg animate-slide-up">
            <CardHeader>
              <CardTitle className="text-2xl mb-2">Envie sua mensagem</CardTitle>
              <p className="text-muted-foreground">
                Preencha o formulário abaixo e entraremos em contato em breve
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Input
                    name="name"
                    placeholder="Seu nome completo"
                    required
                    className="h-12 text-base"
                  />
                </div>
                
                <div>
                  <Input
                    name="email"
                    type="email"
                    placeholder="Seu melhor email"
                    required
                    className="h-12 text-base"
                  />
                </div>
                
                <div>
                  <Textarea
                    name="message"
                    placeholder="Conte-nos sobre seu projeto e como podemos ajudar..."
                    required
                    className="min-h-[120px] text-base resize-none"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className="w-full h-12 text-base font-semibold"
                >
                  {isSubmitting ? (
                    "Enviando..."
                  ) : (
                    <>
                      Enviar Mensagem
                      <Send className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Info */}
          <div className="space-y-8 animate-slide-up" style={{ animationDelay: '200ms' }}>
            <Card className="border-0 bg-gradient-card shadow-card">
              <CardContent className="p-8">
                <div className="flex items-start">
                  <Mail className="h-8 w-8 text-primary mt-1 mr-4 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Email</h3>
                    <p className="text-muted-foreground mb-3">
                      Resposta em até 24 horas
                    </p>
                    <a 
                      href="mailto:contato@datavizbi.com.br"
                      className="text-primary hover:underline font-medium"
                    >
                      contato@datavizbi.com.br
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 bg-gradient-card shadow-card">
              <CardContent className="p-8">
                <div className="flex items-start">
                  <MapPin className="h-8 w-8 text-primary mt-1 mr-4 flex-shrink-0" />
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Localização</h3>
                    <p className="text-muted-foreground mb-3">
                      Atendemos todo o Brasil
                    </p>
                    <p className="text-foreground font-medium">Brasília, DF</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CTA Card */}
            <Card className="border-0 bg-gradient-primary text-white">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-semibold mb-4">
                  Prefere falar diretamente?
                </h3>
                <p className="mb-6 opacity-90">
                  Clique no botão do WhatsApp e fale conosco agora mesmo
                </p>
                <div className="flex items-center justify-center text-accent text-lg font-semibold">
                  <span className="w-3 h-3 bg-accent rounded-full mr-2 animate-pulse"></span>
                  Online agora
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};